import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enTranslation from './locales/en/translation.json';
import esTranslation from './locales/es/translation.json';

// Supported languages configuration
// To add a new language:
// 1. Create a new translation file in src/i18n/locales/{lang}/translation.json
// 2. Import it above
// 3. Add it to the resources object below
// 4. Add it to supportedLngs array
export const supportedLanguages = [
  { code: 'en', name: 'English', flag: '🇬🇧', flagCode: 'gb' },
  { code: 'es', name: 'Español', flag: '🇪🇸', flagCode: 'es' },
];

// Flat allowlist for query-param / API validation — derived from the
// canonical list above so adding a locale is a one-line change.
export const supportedLanguageCodes = supportedLanguages.map((l) => l.code);

/**
 * Apply a raw ?lang= query value to the i18n runtime if it is a supported
 * code. Returns the normalized code on success, or null if the value is
 * missing / unsupported. i18next persists the choice to localStorage
 * automatically via `caches: ['localStorage']`, so callers do NOT need to
 * touch localStorage themselves.
 */
export const applyQueryLanguage = (raw) => {
  if (!raw || !supportedLanguageCodes.includes(raw)) return null;
  if (i18n.language !== raw) i18n.changeLanguage(raw);
  return raw;
};

/**
 * Apply a locale read from the URL pathname (e.g. `/es/login`). Returns the
 * normalized code on success, or null if the pathname is not locale-prefixed.
 * Path-based locale is the canonical source on public auth pages; this helper
 * is called from route components so changes take effect on navigation too.
 */
export const applyPathLanguage = (pathname) => {
  const m = pathname?.match(/^\/(en|es)(\/|$)/);
  if (!m) return null;
  const raw = m[1];
  if (i18n.language !== raw) i18n.changeLanguage(raw);
  return raw;
};

const resources = {
  en: {
    translation: enTranslation,
  },
  es: {
    translation: esTranslation,
  },
};

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: supportedLanguages.map(lang => lang.code),
    
    // Language detection options
    detection: {
      // Order of language detection
      order: ['localStorage', 'navigator', 'htmlTag'],
      // Keys to lookup language from
      lookupLocalStorage: 'bloom-language',
      // Cache user language in localStorage
      caches: ['localStorage'],
    },
    
    interpolation: {
      // React already handles escaping
      escapeValue: false,
    },
    
    // React options
    react: {
      useSuspense: false,
    },
  });

export default i18n;

